import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Users, Calendar, MapPin, CheckCircle, Clock, XCircle, Trash2 } from 'lucide-react';
import useDemoDates from '@/hooks/useDemoDates';
import AddDateDialog from './AddDateDialog';
import { AddToCalendarButton } from '@/components/native/ContactActionButtons';

const FamilyTab = () => {
  const { dates, loading, addDate, deleteDate } = useDemoDates('family');
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this event? You can always add it again later.')) return;
    await deleteDate(id);
  };

  const handleDateAdded = async (dateData) => {
    await addDate(dateData);
    setDialogOpen(false);
  };

  const getStatusBadge = (status) => {
    const config = {
      planned: { label: 'Planned', color: 'bg-blue-100 text-blue-700', icon: Clock },
      completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle }
    };
    const cfg = config[status] || config.planned;
    return (
      <Badge className={cfg.color}>
        <cfg.icon className="h-3 w-3 mr-1" />
        {cfg.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Shared Events & Agreements</h3>
          <p className="text-sm text-gray-500">Track shared commitments and events</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900 whitespace-nowrap flex-shrink-0"
          data-testid="add-family-event-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Event
        </Button>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Users className="h-10 w-10 mx-auto text-[#C5D9ED] mb-3" />
          <h4 className="text-base font-medium text-gray-900 mb-1">No shared events yet</h4>
          <p className="text-sm text-gray-600">
            Add an event to keep everyone informed and on the same page.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((item) => (
            <Card key={item.id} className="border-l-4 border-l-[#C5D9ED]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h4 className="font-medium text-gray-900">{item.title}</h4>
                      {item.agreement_status && getStatusBadge(item.agreement_status)}
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                          {item.time && ` at ${item.time}`}
                        </span>
                        {item.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {item.location}
                          </span>
                        )}
                      </div>
                      
                      {item.agreement_what && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-blue-800">
                          <strong>You agreed to:</strong> {item.agreement_what}
                          {item.agreement_who && item.agreement_who.length > 0 && (
                            <span className="ml-2 text-blue-600">(with {item.agreement_who.join(', ')})</span>
                          )}
                        </div>
                      )}
                      
                      {item.linked_family_members && item.linked_family_members.length > 0 && (
                        <div className="flex items-center gap-1 text-gray-500">
                          <Users className="h-3.5 w-3.5" />
                          {item.linked_family_members.join(', ')}
                        </div>
                      )}
                      
                      {item.notes && (
                        <p className="text-gray-500 italic mt-1">{item.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <AddToCalendarButton
                      dateRecord={{
                        title: item.title,
                        date: item.date,
                        time: item.time,
                        location: item.location || '',
                        type: 'family',
                        all_day: !item.time,
                        reminder_minutes: 60,
                        notes: item.agreement_what || item.notes || ''
                      }}
                      size="sm"
                      variant="ghost"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      data-testid={`add-to-calendar-${item.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AddDateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category="family"
        onSuccess={handleDateAdded}
      />
    </>
  );
};

export default FamilyTab;
